import { useGetAllEncounters } from '_api';
import { ConstantsNameDescriptions, CommunicationType } from '_api/types';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useMemo } from 'react';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface useEncounterProps {
  patientId?: number;
}

const useEncounter = ({ patientId }: useEncounterProps) => {
  const exchangeEmailOptions: ConstantsNameDescriptions<boolean>[] = [
    { name: true, description: 'Yes' },
    { name: false, description: 'No' },
  ];

  const communicationOptions: ConstantsNameDescriptions<CommunicationType>[] = [
    { name: 'PHONE', description: 'Phone' },
    { name: 'VIDEO', description: 'Video' },
  ];

  const { data: encounters = [], isLoading } =
    patientId != undefined
      ? useGetAllEncounters(patientId, {
          page: 0,
          size: 1000,
        })
      : {
          data: [],
          isLoading: false,
        };

  const now = dayjs();

  const isNow = (encounterDate: dayjs.Dayjs) =>
    now.date() === encounterDate.date() &&
    now.month() === encounterDate.month() &&
    now.year() === encounterDate.year();

  const convertToDayjs = (date: string) => dayjs(date);

  const dailyEncounters = useMemo(
    () =>
      encounters.filter((encounter) => {
        const encounterDate = convertToDayjs(encounter.date_of_observation);
        return isNow(encounterDate);
      }),
    [encounters],
  );

  const weeklyEncounters = useMemo(
    () =>
      encounters.filter((encounter) => {
        const encounterDate = convertToDayjs(encounter.date_of_observation);
        return (
          encounterDate.isAfter(now.subtract(1, 'week')) &&
          encounterDate.isSameOrBefore(now)
        );
      }),
    [encounters],
  );
  const monthlyEncounters = useMemo(
    () =>
      encounters.filter((encounter) => {
        const encounterDate = convertToDayjs(encounter.date_of_observation);
        return (
          encounterDate.isAfter(now.subtract(1, 'month')) &&
          encounterDate.isSameOrBefore(now)
        );
      }),
    [encounters],
  );

  const customFilter = (startDate: number, endDate: number) => {
    const custom = encounters.filter((encounter) => {
      const encounterDate = convertToDayjs(encounter.date_of_observation);
      return (
        encounterDate.isSameOrAfter(dayjs(startDate), 'day') &&
        encounterDate.isSameOrBefore(dayjs(endDate), 'day')
      );
    });
    return custom;
  };

  return {
    communicationOptions,
    customFilter,
    dailyEncounters,
    exchangeEmailOptions,
    encounters,
    isLoading,
    monthlyEncounters,
    weeklyEncounters,
  };
};

export default useEncounter;
